import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Sparkles, Trash2, Copy, ExternalLink, Volume2, Loader2, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  generateAdCampaign, saveAdCampaign, listAdCampaigns,
  deleteAdCampaign, generateAdVoiceover, generateAdVideoAssets,
} from "@/lib/api/ads-studio.functions";
import { stitchAdVideo } from "@/lib/ads-video-stitcher";
import { Film, Download } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/ads-studio")({
  component: AdsStudio,
});

const PLATFORMS = ["facebook", "tiktok", "linkedin", "instagram"] as const;
type Platform = (typeof PLATFORMS)[number];

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success("Copied"),
    () => toast.error("Copy failed"),
  );
}

function AdsStudio() {
  const qc = useQueryClient();
  const listFn = useServerFn(listAdCampaigns);
  const genFn = useServerFn(generateAdCampaign);
  const saveFn = useServerFn(saveAdCampaign);
  const delFn = useServerFn(deleteAdCampaign);
  const voFn = useServerFn(generateAdVoiceover);
  const videoFn = useServerFn(generateAdVideoAssets);

  const list = useQuery({ queryKey: ["ads-campaigns"], queryFn: () => listFn({ data: {} } as any) });
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoBuilding, setVideoBuilding] = useState(false);

  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState<"awareness" | "leads" | "bookings" | "engagement" | "signups">("engagement");
  const [tone, setTone] = useState<"authoritative" | "friendly" | "bold" | "empathetic" | "witty">("authoritative");
  const [platforms, setPlatforms] = useState<Platform[]>(["facebook", "tiktok", "linkedin"]);
  const [result, setResult] = useState<any>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [voiceUrl, setVoiceUrl] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: async () =>
      genFn({ data: { title, brief, audience, goal, platforms, tone } } as any),
    onSuccess: (data) => {
      setResult(data);
      setEditingId(null);
      toast.success("Ad campaign generated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Generation failed"),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!result) return null;
      return saveFn({
        data: {
          id: editingId ?? undefined,
          title, brief, audience, goal, platforms,
          variants: result.variants ?? {},
          visual_prompts: result.visual_prompts ?? [],
          hashtags: result.hashtags ?? [],
          canva_search_url: result.canva_search_url ?? null,
          status: "ready",
        },
      } as any);
    },
    onSuccess: (row: any) => {
      if (row?.id) setEditingId(row.id);
      qc.invalidateQueries({ queryKey: ["ads-campaigns"] });
      toast.success("Saved to campaigns");
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const del = useMutation({
    mutationFn: async (id: string) => delFn({ data: { id } } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ads-campaigns"] });
      toast.success("Deleted");
    },
  });

  const voice = useMutation({
    mutationFn: async (text: string) => voFn({ data: { text } } as any),
    onSuccess: (data: any) => {
      setVoiceUrl(`data:${data.mime};base64,${data.base64}`);
      toast.success("Voiceover ready");
    },
    onError: (e: any) => toast.error(e?.message ?? "Voiceover failed"),
  });

  const buildVideo = useMutation({
    mutationFn: async () => {
      const scenes = (variants?.tiktok?.scenes ?? []) as any[];
      if (!scenes.length) throw new Error("Generate a TikTok script first (needs scenes).");
      setVideoBuilding(true);
      setVideoProgress(0);
      setVideoUrl(null);
      try {
        const assets = await videoFn({
          data: {
            title,
            brand_context: "",
            scenes: scenes.slice(0, 6).map((s: any) => ({
              on_screen_text: String(s.on_screen_text ?? "").slice(0, 240),
              voiceover: String(s.voiceover ?? "").slice(0, 600),
              b_roll: String(s.b_roll ?? "").slice(0, 400),
            })),
          },
        } as any);
        const blob = await stitchAdVideo(assets.scenes, {
          onProgress: (p) => setVideoProgress(p),
        });
        setVideoUrl(URL.createObjectURL(blob));
      } finally {
        setVideoBuilding(false);
      }
    },
    onSuccess: () => toast.success("Video ad ready"),
    onError: (e: any) => toast.error(e?.message ?? "Video build failed"),
  });

  function togglePlatform(p: Platform) {
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  }

  function loadCampaign(row: any) {
    setTitle(row.title); setBrief(row.brief); setAudience(row.audience ?? "");
    setGoal(row.goal); setPlatforms(row.platforms ?? []);
    setResult({
      variants: row.variants ?? {},
      visual_prompts: row.visual_prompts ?? [],
      hashtags: row.hashtags ?? [],
      canva_search_url: row.canva_search_url,
    });
    setEditingId(row.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const variants = result?.variants ?? {};
  const visualPrompts: any[] = result?.visual_prompts ?? [];
  const hashtags: string[] = result?.hashtags ?? [];

  const fullCaption = useMemo(() => {
    if (!variants?.facebook) return "";
    const fb = variants.facebook;
    return [fb.hook, "", fb.primary_text, "", (hashtags || []).slice(0, 10).join(" ")]
      .filter(Boolean).join("\n");
  }, [variants, hashtags]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex items-start gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-md bg-navy text-navy-foreground">
          <Megaphone className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl text-navy">Ads Studio</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Draft a single brief and get Facebook, TikTok, LinkedIn and Instagram-ready copy plus visual
            directions for Canva, ElevenLabs voiceovers, and one-click AI video ads. Copy is written by
            Gemini 3.6 Flash, visuals are directed by GPT-5.4-mini, video frames use Gemini 2.5 Flash Image.
          </p>
          <p className="text-xs mt-1 font-medium text-emerald-700">
            Admin-only tool — everything here (copy, visuals, voiceovers, and video ads) is free and does
            not consume account credits.
          </p>
        </div>
      </header>

      <Card>
        <CardHeader><CardTitle>Campaign brief</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Free CCMA referral checklist" />
            </div>
            <div>
              <Label>Audience</Label>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g. SA employees dismissed in the last 30 days" />
            </div>
          </div>
          <div>
            <Label>Brief</Label>
            <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={5}
              placeholder="What are we promoting? Key benefit, offer, and call-to-action." />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label>Goal</Label>
              <Select value={goal} onValueChange={(v: any) => setGoal(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["awareness", "leads", "bookings", "engagement", "signups"].map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tone</Label>
              <Select value={tone} onValueChange={(v: any) => setTone(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["authoritative", "friendly", "bold", "empathetic", "witty"].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Platforms</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <Badge
                    key={p}
                    variant={platforms.includes(p) ? "default" : "outline"}
                    className="cursor-pointer capitalize"
                    onClick={() => togglePlatform(p)}
                  >
                    {p}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => generate.mutate()}
              disabled={generate.isPending || !title.trim() || !brief.trim() || platforms.length === 0}
            >
              {generate.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Generate ad campaign
            </Button>
            {result && (
              <Button variant="secondary" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                {editingId ? "Update" : "Save"} campaign
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Generated variants</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={platforms[0]}>
              <TabsList className="flex-wrap">
                {platforms.map((p) => (
                  <TabsTrigger key={p} value={p} className="capitalize">{p}</TabsTrigger>
                ))}
                <TabsTrigger value="visuals">Visuals</TabsTrigger>
                <TabsTrigger value="hashtags">Hashtags</TabsTrigger>
                <TabsTrigger value="video">Video ad</TabsTrigger>
              </TabsList>

              {/* Facebook */}
              {platforms.includes("facebook") && (
                <TabsContent value="facebook" className="space-y-4 pt-4">
                  <FieldRow label="Hook" value={variants.facebook?.hook} />
                  <FieldRow label="Primary text" value={variants.facebook?.primary_text} multiline />
                  <div className="grid gap-3 md:grid-cols-3">
                    <FieldRow label="Headline" value={variants.facebook?.headline} />
                    <FieldRow label="Description" value={variants.facebook?.description} />
                    <FieldRow label="CTA" value={variants.facebook?.cta} />
                  </div>
                  {Array.isArray(variants.facebook?.carousel) && variants.facebook.carousel.length > 0 && (
                    <div>
                      <Label>Carousel slides</Label>
                      <div className="mt-2 grid gap-2 md:grid-cols-2">
                        {variants.facebook.carousel.map((s: any, i: number) => (
                          <div key={i} className="rounded border p-3 text-sm">
                            <p className="font-semibold text-navy">{i + 1}. {s.heading}</p>
                            <p className="text-muted-foreground">{s.body}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(fullCaption)}>
                      <Copy className="mr-2 h-3.5 w-3.5" /> Copy full caption + hashtags
                    </Button>
                    {result.canva_search_url && (
                      <a href={result.canva_search_url} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="mr-2 h-3.5 w-3.5" /> Find matching Canva template
                        </Button>
                      </a>
                    )}
                  </div>
                </TabsContent>
              )}

              {/* TikTok */}
              {platforms.includes("tiktok") && (
                <TabsContent value="tiktok" className="space-y-4 pt-4">
                  <FieldRow label="Caption" value={variants.tiktok?.caption} multiline />
                  <div>
                    <Label>Scenes</Label>
                    <div className="mt-2 space-y-2">
                      {(variants.tiktok?.scenes ?? []).map((s: any, i: number) => (
                        <div key={i} className="rounded border p-3 text-sm space-y-1">
                          <p className="font-semibold text-navy">Scene {i + 1}</p>
                          <p><span className="text-muted-foreground">On-screen:</span> {s.on_screen_text}</p>
                          <p><span className="text-muted-foreground">VO:</span> {s.voiceover}</p>
                          <p><span className="text-muted-foreground">B-roll:</span> {s.b_roll}</p>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => voice.mutate(s.voiceover)}
                            disabled={voice.isPending}
                          >
                            <Volume2 className="mr-2 h-3.5 w-3.5" />
                            {voice.isPending ? "Generating…" : "ElevenLabs voiceover"}
                          </Button>
                        </div>
                      ))}
                    </div>
                    {voiceUrl && (
                      <audio controls src={voiceUrl} className="mt-3 w-full" />
                    )}
                  </div>
                  {Array.isArray(variants.tiktok?.hashtags) && (
                    <div className="flex flex-wrap gap-1">
                      {variants.tiktok.hashtags.map((h: string, i: number) => (
                        <Badge key={i} variant="secondary">{h}</Badge>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}

              {/* LinkedIn */}
              {platforms.includes("linkedin") && (
                <TabsContent value="linkedin" className="space-y-3 pt-4">
                  <FieldRow label="Hook" value={variants.linkedin?.hook} />
                  <FieldRow label="Body" value={variants.linkedin?.body} multiline />
                  <FieldRow label="CTA" value={variants.linkedin?.cta} />
                  {Array.isArray(variants.linkedin?.hashtags) && (
                    <div className="flex flex-wrap gap-1">
                      {variants.linkedin.hashtags.map((h: string, i: number) => (
                        <Badge key={i} variant="secondary">{h}</Badge>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Instagram */}
              {platforms.includes("instagram") && (
                <TabsContent value="instagram" className="space-y-3 pt-4">
                  <FieldRow label="Caption" value={variants.instagram?.caption} multiline />
                  {Array.isArray(variants.instagram?.first_comment_hashtags) && (
                    <div className="flex flex-wrap gap-1">
                      {variants.instagram.first_comment_hashtags.map((h: string, i: number) => (
                        <Badge key={i} variant="secondary">{h}</Badge>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Visuals */}
              <TabsContent value="visuals" className="pt-4 space-y-3">
                {visualPrompts.length === 0 && <p className="text-sm text-muted-foreground">No visual concepts returned.</p>}
                {visualPrompts.map((v, i) => (
                  <div key={i} className="rounded border p-3 text-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-navy">{v.label} <span className="text-xs text-muted-foreground">({v.platform})</span></p>
                      <a
                        href={`https://www.canva.com/search/templates?q=${encodeURIComponent(v.canva_query ?? v.label ?? "")}`}
                        target="_blank" rel="noreferrer"
                      >
                        <Button size="sm" variant="outline">
                          <ExternalLink className="mr-2 h-3.5 w-3.5" /> Canva templates
                        </Button>
                      </a>
                    </div>
                    <p className="text-muted-foreground">{v.prompt}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(v.prompt)}>
                        <Copy className="mr-2 h-3.5 w-3.5" /> Copy prompt
                      </Button>
                    </div>
                  </div>
                ))}
              </TabsContent>

              {/* Hashtags */}
              <TabsContent value="hashtags" className="pt-4">
                <div className="flex flex-wrap gap-1">
                  {hashtags.map((h, i) => (
                    <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => copyToClipboard(h)}>{h}</Badge>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-3"
                  onClick={() => copyToClipboard(hashtags.join(" "))}>
                  <Copy className="mr-2 h-3.5 w-3.5" /> Copy all
                </Button>
              </TabsContent>

              {/* Video ad */}
              <TabsContent value="video" className="pt-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Builds a vertical 9:16 slideshow ad using the TikTok scenes. Each scene gets a
                  photorealistic AI background (Gemini 2.5 Flash Image) plus an ElevenLabs voiceover,
                  stitched together in your browser into a downloadable WebM. Free for admins.
                </p>
                {!variants?.tiktok?.scenes?.length && (
                  <p className="text-sm text-amber-700">
                    Include TikTok in your platforms and re-generate — the video builder needs the scene
                    breakdown to work.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={() => buildVideo.mutate()}
                    disabled={buildVideo.isPending || videoBuilding || !variants?.tiktok?.scenes?.length}
                  >
                    {videoBuilding ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Film className="mr-2 h-4 w-4" />
                    )}
                    {videoBuilding ? "Rendering…" : "Generate video ad (free)"}
                  </Button>
                  {videoBuilding && (
                    <span className="text-xs text-muted-foreground">
                      {Math.round(videoProgress * 100)}%
                    </span>
                  )}
                  {videoUrl && (
                    <a href={videoUrl} download={`${(title || "ad").replace(/\s+/g, "-").toLowerCase()}.webm`}>
                      <Button size="sm" variant="outline">
                        <Download className="mr-2 h-3.5 w-3.5" /> Download .webm
                      </Button>
                    </a>
                  )}
                </div>
                {videoUrl && (
                  <video src={videoUrl} controls className="mt-3 w-full max-w-sm rounded border" />
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Saved campaigns</CardTitle></CardHeader>
        <CardContent>
          {list.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {list.data?.length === 0 && <p className="text-sm text-muted-foreground">No campaigns yet.</p>}
          <div className="space-y-2">
            {list.data?.map((row: any) => (
              <div key={row.id} className="flex items-start justify-between gap-3 rounded border p-3">
                <div>
                  <p className="font-semibold text-navy">{row.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {(row.platforms ?? []).join(" • ")} · {new Date(row.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{row.brief}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => loadCampaign(row)}>Open</Button>
                  <Button size="sm" variant="ghost" onClick={() => del.mutate(row.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FieldRow({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  const v = value ?? "";
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(v)}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
      {multiline ? (
        <Textarea value={v} readOnly rows={4} className="mt-1" />
      ) : (
        <Input value={v} readOnly className="mt-1" />
      )}
    </div>
  );
}
